import { HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FirebaseOptions, getApps, initializeApp } from 'firebase/app';
import {
  Firestore,
  addDoc,
  collection,
  getFirestore,
  serverTimestamp,
} from 'firebase/firestore';
import { environment } from 'src/environments/environment';
import { MemberSessionService } from './member-session.service';

export const LOGGING_SKIP_HEADER = 'X-LOGGING-SKIP';

interface HttpLogInput {
  transport: 'angular-http';
  method: string;
  url: string;
  requestHeaders?: Record<string, unknown>;
  requestBody?: unknown;
  responseStatus?: number;
  responseHeaders?: Record<string, unknown>;
  responseBody?: unknown;
  error?: unknown;
  durationMs?: number;
  correlationId?: string;
  success: boolean;
}

@Injectable({ providedIn: 'root' })
export class StructuredLoggerService {
  private readonly memberSession = inject(MemberSessionService);

  private firestore: Firestore | null = null;
  private initialized = false;

  private readonly loggingEnabled = environment.structuredLogging.enabled === true;
  private readonly httpCollection = environment.structuredLogging.httpCollection || 'httpLogs';
  private readonly exceptionCollection =
    environment.structuredLogging.exceptionCollection || 'exceptionLogs';
  private readonly maxPayloadChars = environment.structuredLogging.maxPayloadChars || 800000;
  private readonly exceptionDedupeWindowMs = Number(
    environment.structuredLogging.exceptionDedupeWindowMs ?? 60000
  );
  private readonly exceptionMaxPerWindow = Number(
    environment.structuredLogging.exceptionMaxPerWindow ?? 3
  );
  private readonly exceptionThrottle = new Map<string, { windowStart: number; count: number }>();

  constructor() {
    if (this.loggingEnabled) {
      this.tryInitialize();
    }
  }

  createCorrelationId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }

  headersToObject(headers?: HttpHeaders | Record<string, unknown>): Record<string, unknown> {
    if (!headers) {
      return {};
    }

    if (headers instanceof HttpHeaders) {
      const out: Record<string, unknown> = {};
      headers.keys().forEach((key) => {
        out[key] = headers.get(key);
      });
      return out;
    }

    return headers;
  }

  logHttp(input: HttpLogInput): void {
    if (!this.loggingEnabled) {
      return;
    }

    const payload = {
      type: 'http',
      success: input.success,
      transport: input.transport,
      correlationId: input.correlationId || this.createCorrelationId(),
      method: input.method,
      url: input.url,
      durationMs: input.durationMs ?? null,
      request: {
        headers: this.limitSize(input.requestHeaders || {}),
        body: this.limitSize(input.requestBody),
      },
      response: {
        status: input.responseStatus ?? null,
        headers: this.limitSize(input.responseHeaders || {}),
        body: this.limitSize(input.responseBody),
      },
      error: this.limitSize(input.error),
      context: this.getContext(),
      createdAt: serverTimestamp(),
    };

    this.write(this.httpCollection, payload);
  }

  logException(error: unknown, source = 'unknown'): void {
    if (!this.loggingEnabled) {
      return;
    }

    const message = (error as { message?: string } | null)?.message || `${error}`;
    const stack = this.limitSize((error as { stack?: string } | null)?.stack ?? null);
    if (this.shouldSkipExceptionLog(source, message, stack)) {
      return;
    }

    this.write(this.exceptionCollection, {
      type: 'exception',
      message,
      stack,
      source: source || 'unknown',
      error: this.normalizeError(error),
      context: this.getContext(),
      createdAt: serverTimestamp(),
    });
  }

  private shouldSkipExceptionLog(source: string, message: string, stack: unknown): boolean {
    const signature = this.getExceptionSignature(source, message, stack);
    const now = Date.now();
    const existing = this.exceptionThrottle.get(signature);

    if (!existing || now - existing.windowStart > this.exceptionDedupeWindowMs) {
      this.exceptionThrottle.set(signature, { windowStart: now, count: 1 });
      this.pruneExceptionThrottleState(now);
      return false;
    }

    existing.count += 1;
    return existing.count > this.exceptionMaxPerWindow;
  }

  private getExceptionSignature(source: string, message: string, stack: unknown): string {
    const route = window.location?.hash || '';
    const stackHead =
      typeof stack === 'string'
        ? stack.split('\n')[0]
        : stack &&
            typeof stack === 'object' &&
            typeof (stack as { value?: string }).value === 'string'
          ? (stack as { value: string }).value.split('\n')[0]
          : '';

    return [source || 'unknown', message || 'unknown', stackHead || '', route].join('|');
  }

  private pruneExceptionThrottleState(now: number): void {
    if (this.exceptionThrottle.size <= 200) {
      return;
    }

    for (const [key, state] of this.exceptionThrottle.entries()) {
      if (now - state.windowStart > this.exceptionDedupeWindowMs * 2) {
        this.exceptionThrottle.delete(key);
      }
    }
  }

  private tryInitialize(): void {
    if (this.initialized) {
      return;
    }

    const firebaseOptions = environment.firebase as FirebaseOptions;
    if (
      !firebaseOptions ||
      !firebaseOptions.apiKey ||
      !firebaseOptions.projectId ||
      !firebaseOptions.appId
    ) {
      console.warn(
        '[StructuredLogger] Firebase config is incomplete. Structured logging disabled.'
      );
      return;
    }

    const app = getApps().length ? getApps()[0] : initializeApp(firebaseOptions);
    this.firestore = getFirestore(app);
    this.initialized = true;
  }

  private write(collectionName: string, payload: unknown): void {
    if (!this.initialized || !this.firestore) {
      return;
    }

    try {
      void addDoc(collection(this.firestore, collectionName), payload).catch((error) => {
        console.warn('[StructuredLogger] Failed to write log document', error);
      });
    } catch (error) {
      console.warn('[StructuredLogger] Failed to write log document', error);
    }
  }

  private getContext(): Record<string, unknown> {
    const session = this.memberSession.getSession();
    const member = session?.member;
    return {
      version: environment.appVersion || 'unknown',
      platform: Capacitor.getPlatform(),
      route: window.location?.hash || '',
      loggedIn: !!session?.cardNumber,
      user: member
        ? {
            authenticatedCardNumber: session?.cardNumber ?? null,
            virtualCardNumber: session?.virtualCard.cardNumber ?? null,
            physicalCardNumber: session?.physicalCard?.cardNumber ?? null,
            cell: member.cell ?? null,
            merchantId: member.merchantId ?? null,
            countryId:
              (member.country as { id?: number | null } | null | undefined)?.id ?? null,
          }
        : null,
    };
  }

  private normalizeError(error: unknown): unknown {
    if (!error) {
      return null;
    }

    if (error instanceof Error) {
      return {
        name: error.name ?? null,
        message: error.message ?? null,
        stack: error.stack ?? null,
      };
    }

    return this.limitSize(error);
  }

  private limitSize(value: unknown): unknown {
    if (value === undefined) {
      return null;
    }

    if (
      value === null ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      typeof value === 'bigint'
    ) {
      return value;
    }

    if (typeof value === 'string') {
      if (value.length <= this.maxPayloadChars) {
        return value;
      }

      return {
        _truncated: true,
        originalLength: value.length,
        value: value.slice(0, this.maxPayloadChars),
      };
    }

    try {
      const serialized = JSON.stringify(value);
      if (serialized.length <= this.maxPayloadChars) {
        return JSON.parse(serialized);
      }

      return {
        _truncated: true,
        originalLength: serialized.length,
        value: serialized.slice(0, this.maxPayloadChars),
      };
    } catch {
      return {
        _serializationError: true,
        value: `${value}`,
      };
    }
  }
}
